import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import V2SEO from '../components/V2SEO';
import {
  portfolioProjects,
  projectCategories,
  photos,
} from '../data/realContent';

const Portfolio = () => {
  const [category, setCategory] = useState('all');
  const [openProject, setOpenProject] = useState(null);

  const filtered = portfolioProjects.filter(
    (p) => category === 'all' || p.category === category
  );

  useEffect(() => {
    document.body.style.overflow = openProject ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [openProject]);

  useEffect(() => {
    if (!openProject) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpenProject(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openProject]);

  return (
    <>
      <V2SEO
        title="Drone Portfolio — Aerial Cinematography & Photography Projects in Pennsylvania"
        description="Client projects from Cosmik Drones: the Discover Lehigh Valley 2025 commercial, PA Gets It Done, Lehigh Valley Phantoms, Reading Royals, Skirmish Paintball, weddings, and aerial photography across Pennsylvania."
        path="/portfolio"
        keywords="drone portfolio, aerial cinematography portfolio, FPV portfolio, drone videographer portfolio Pennsylvania"
      />

      <section className="v2-svc-hero">
        <div className="v2-container">
          <span className="v2-eyebrow">Portfolio</span>
          <h1>Client Projects</h1>
          <p className="v2-prose" style={{ marginTop: '1rem' }}>
            Aerial cinematography and photography delivered for tourism boards, professional
            sports teams, commercial productions, and private clients across Pennsylvania
            and the East Coast. Select any project for full details.
          </p>
        </div>
      </section>

      <section className="v2-section v2-section--tight">
        <div className="v2-container">
          <div className="v2-filter">
            {projectCategories.map((c) => (
              <button
                key={c.id}
                className={category === c.id ? 'is-active' : ''}
                onClick={() => setCategory(c.id)}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: '1.2rem',
          }}>
            {filtered.map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={() => setOpenProject(p)} />
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-shead">
            <div>
              <span className="v2-eyebrow">Photography</span>
              <h2>Aerial Stills</h2>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
          }}>
            {photos.map((p) => (
              <div key={p.src} style={{
                borderRadius: '20px',
                overflow: 'hidden',
                aspectRatio: '4 / 3',
                border: '1px solid var(--v2-line)',
                background: 'var(--v2-bg-card)',
              }}>
                <img
                  src={p.src}
                  alt={p.alt}
                  loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section v2-section--tight">
        <div className="v2-container">
          <div className="v2-cta">
            <h2>Interested in working together?</h2>
            <p>Every project starts with a conversation about your goals, timeline, and budget.</p>
            <Link to="/contact" className="v2-btn v2-btn--primary">Request a Quote</Link>
          </div>
        </div>
      </section>

      {openProject && (
        <ProjectModal project={openProject} onClose={() => setOpenProject(null)} />
      )}
    </>
  );
};

const ProjectCard = ({ project, onOpen }) => (
  <button
    onClick={onOpen}
    style={{
      textAlign: 'left',
      border: '1px solid var(--v2-line)',
      borderRadius: '22px',
      overflow: 'hidden',
      background: 'rgba(255,255,255,0.02)',
      color: 'inherit',
      cursor: 'pointer',
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      font: 'inherit',
    }}
    aria-label={`View details for ${project.title}`}
  >
    <div style={{ position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden', background: 'var(--v2-bg-card)' }}>
      <span className="v2-telemetry">{project.location}</span>
      <img
        src={project.thumbnail}
        alt={project.title}
        loading="lazy"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
    <div style={{ padding: '1.4rem 1.5rem 1.6rem' }}>
      <h3 style={{ margin: '0 0 0.4rem', fontSize: '1.2rem' }}>{project.title}</h3>
      <p style={{ margin: 0, color: 'var(--v2-text-mute)', fontSize: '0.88rem' }}>
        {project.client}
      </p>
      <span className="v2-service__cta" style={{ marginTop: '0.9rem', display: 'inline-block' }}>
        View Details
      </span>
    </div>
  </button>
);

const ProjectModal = ({ project, onClose }) => (
  <div className="v2-modal" role="dialog" aria-modal="true" aria-label={project.title} onClick={onClose}>
    <div className="v2-modal__box" style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <button className="v2-modal__close" onClick={onClose} aria-label="Close project details">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
        </svg>
      </button>
      <div className="v2-modal__media">
        <video
          src={project.video}
          poster={project.thumbnail}
          controls
          autoPlay
          muted={!project.hasSound}
          playsInline
        />
      </div>
      <div className="v2-modal__body">
        <span className="v2-eyebrow">{project.location}</span>
        <h2 style={{ margin: '0.5rem 0 0.8rem', fontSize: 'clamp(1.4rem, 3vw, 2rem)' }}>{project.title}</h2>
        <p style={{ color: 'var(--v2-text-dim)', lineHeight: 1.65, margin: 0 }}>{project.description}</p>
        <div className="v2-modal__meta">
          <div>
            <h4>Client</h4>
            <p>{project.client}</p>
          </div>
          <div>
            <h4>Location</h4>
            <p>{project.location}</p>
          </div>
          <div>
            <h4>Deliverables</h4>
            <p>{project.deliverables}</p>
          </div>
        </div>
        {project.externalUrl && (
          <a
            href={project.externalUrl}
            target="_blank"
            rel="noreferrer"
            className="v2-btn v2-btn--ghost"
            style={{ marginTop: '1.6rem' }}
          >
            Visit Project Website
          </a>
        )}
      </div>
    </div>
  </div>
);

export default Portfolio;
